import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Loader2, Sparkles, ExternalLink, ChevronDown, ChevronUp, CheckCircle2, XCircle, MinusCircle, Wrench } from "lucide-react";
import { api } from "@/lib/api";

/**
 * DossierInstitutions · Multi-institution comparator section.
 *
 * Reads /api/institutions/for-dossier/:id on mount and offers a
 * "Comparer maintenant" CTA that triggers /api/institutions/compare/dossier/:id.
 * Every institution card unfolds to reveal:
 *   • the best product & its fit score (with grade badge)
 *   • the checks that passed / failed (weight-ordered)
 *   • the public source link + last verification date
 */
export function DossierInstitutions({ dossier }) {
  const dossierId = dossier?.dossier_id;
  const [comparison, setComparison] = useState(null);
  const [running, setRunning] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!dossierId) return;
    (async () => {
      try {
        const { data } = await api.get(`/institutions/for-dossier/${dossierId}`);
        if (data?.results?.length) setComparison(data);
      } catch { /* no comparison yet — silent */ }
    })();
  }, [dossierId]);

  const runCompare = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/institutions/compare/dossier/${dossierId}`);
      setComparison(data);
      toast.success(`${data?.catalog_size || 0} institutions comparées.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Comparaison impossible.");
    } finally { setRunning(false); }
  };

  const results = comparison?.results || [];

  return (
    <section id="dossier-institutions-section" className="mt-10" data-testid="dossier-institutions">
      <div className="rounded-2xl border border-[#E4E1D8] bg-white overflow-hidden">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-b border-[#E4E1D8] px-6 py-4 bg-[#FBF6EA]/40">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">
              Comparateur multi-institutions
            </div>
            <div className="mt-1 font-display text-xl text-[#0B2545] tracking-tight">
              Où votre dossier a le plus de chances d'être accepté
            </div>
            <div className="text-xs text-[#5B5B62] mt-0.5">
              {comparison
                ? `${comparison.catalog_size} institutions ${comparison.country_used ? `(${comparison.country_used})` : ""} · méthode ${comparison.method_version}`
                : "Cliquez pour comparer votre dossier à toutes les institutions répertoriées de votre pays."}
            </div>
          </div>
          <button
            onClick={runCompare}
            disabled={running || !dossierId}
            data-testid="dossier-institutions-compare"
            className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-60 text-white px-4 py-2.5 text-sm font-medium transition-colors"
          >
            {running
              ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              : <Sparkles className="h-4 w-4" strokeWidth={2} />}
            {running ? "Comparaison en cours…" : comparison ? "Actualiser la comparaison" : "Lancer la comparaison"}
          </button>
        </div>

        {/* Empty state */}
        {!comparison && !running && (
          <div className="px-6 py-12 text-center">
            <Landmark className="h-8 w-8 text-[#7A5B14] mx-auto" strokeWidth={1.4} />
            <p className="mt-4 text-sm text-[#5B5B62] max-w-md mx-auto leading-relaxed">
              Le comparateur croise vos données de bancabilité avec les critères publics de chaque
              institution pour chiffrer votre <span className="font-semibold text-[#0B2545]">score de compatibilité</span>
              &nbsp;(fit-score) et vous indiquer les produits éligibles.
            </p>
          </div>
        )}

        {/* Results list */}
        {results.length > 0 && (
          <div className="divide-y divide-[#E4E1D8]" data-testid="dossier-institutions-list">
            {results.map((inst, idx) => (
              <InstitutionCard
                key={inst.institution_id}
                inst={inst}
                rank={idx + 1}
                expanded={expandedId === inst.institution_id}
                onToggle={() => setExpandedId(expandedId === inst.institution_id ? null : inst.institution_id)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}


function InstitutionCard({ inst, rank, expanded, onToggle }) {
  const best = inst.best_product;
  const grade = best?.grade || "—";
  const fit = inst.overall_fit ?? 0;

  return (
    <div className="px-6 py-4" data-testid={`dossier-institution-card-${inst.institution_id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left flex items-center gap-4 group"
        data-testid={`dossier-institution-toggle-${inst.institution_id}`}
      >
        {/* Rank */}
        <div className="w-8 flex-shrink-0 text-center font-mono text-[10px] uppercase tracking-widest text-[#8B8B8F]">
          #{String(rank).padStart(2, "0")}
        </div>
        {/* Logo letter */}
        <div className="h-11 w-11 rounded-md flex items-center justify-center flex-shrink-0 font-display text-lg text-white"
             style={{ backgroundColor: inst.brand_color || "#0B2545" }}>
          {inst.logo_letter || inst.short_name?.charAt(0) || "?"}
        </div>
        {/* Name + focus */}
        <div className="flex-1 min-w-0">
          <div className="font-display text-base text-[#1F2937] tracking-tight leading-tight">{inst.name}</div>
          <div className="text-xs text-[#5B5B62] truncate max-w-2xl">
            {best?.label_fr || inst.focus_fr}
          </div>
        </div>
        {/* Fit score + grade */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <GradeBadge grade={grade} />
          <div className="text-right min-w-[70px]">
            <div className="font-mono text-2xl text-[#0B2545] tabular-nums leading-none">{fit.toFixed(0)}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-[#8B8B8F]">fit-score</div>
          </div>
          {expanded
            ? <ChevronUp className="h-4 w-4 text-[#5B5B62] group-hover:text-[#0B2545]" strokeWidth={2} />
            : <ChevronDown className="h-4 w-4 text-[#5B5B62] group-hover:text-[#0B2545]" strokeWidth={2} />}
        </div>
      </button>

      {expanded && (
        <div className="mt-4 pl-14 pr-2 pb-2 space-y-6"
             data-testid={`dossier-institution-detail-${inst.institution_id}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Best product summary */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">Meilleur produit</div>
              <div className="mt-1 font-display text-base text-[#1F2937] tracking-tight">
                {best?.label_fr || "—"}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${
                  best?.eligible ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                }`}>
                  {best?.eligible ? <CheckCircle2 className="h-3 w-3" strokeWidth={2} /> : <XCircle className="h-3 w-3" strokeWidth={2} />}
                  {best?.eligible ? "Profil éligible" : "Points à corriger"}
                </span>
                {best?.region_bonus_points > 0 && (
                  <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-navy-50 text-navy-700 bg-[#F1EFE8]">
                    Bonus région
                  </span>
                )}
              </div>
              {best?.reason_fr && (
                <p className="mt-3 text-xs text-[#5B5B62] italic">{best.reason_fr}</p>
              )}
              <a
                href={inst.public_source}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`dossier-institution-source-${inst.institution_id}`}
                className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#0B2545] hover:underline"
              >
                Source publique <ExternalLink className="h-3 w-3" strokeWidth={2} />
              </a>
              {inst.last_verified_at && (
                <div className="mt-1 text-[10px] font-mono text-[#8B8B8F]">Vérifiée le {inst.last_verified_at}</div>
              )}
            </div>

            {/* Checks */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">Détail des critères</div>
              <ul className="mt-2 space-y-1.5">
                {(best?.checks || []).map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    {c.ok
                      ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-600 flex-shrink-0" strokeWidth={2} />
                      : c.weight >= 15
                        ? <XCircle className="h-3.5 w-3.5 mt-0.5 text-amber-600 flex-shrink-0" strokeWidth={2} />
                        : <MinusCircle className="h-3.5 w-3.5 mt-0.5 text-[#8B8B8F] flex-shrink-0" strokeWidth={2} />}
                    <span className="flex-1">
                      <span className="text-[#1F2937]">{c.label_fr}</span>
                      {c.actual && c.target && (
                        <span className="ml-2 text-[#5B5B62]">
                          · <span className={c.ok ? "text-emerald-700" : "text-amber-700"}>{c.actual}</span>
                          <span className="opacity-60"> / cible {c.target}</span>
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Remediation plan — ordered by ROI (impact / effort) */}
          {(best?.remediation || []).length > 0 && (
            <div className="rounded-lg border border-[#E4E1D8] bg-[#FBF6EA]/40 p-4"
                 data-testid={`dossier-institution-remediation-${inst.institution_id}`}>
              <div className="flex items-center gap-2">
                <Wrench className="h-3.5 w-3.5 text-[#7A5B14]" strokeWidth={1.8} />
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                  Plan d'amélioration · {best.remediation.length} action{best.remediation.length > 1 ? "s" : ""}
                </div>
              </div>
              <ol className="mt-3 space-y-2.5">
                {best.remediation.slice(0, 5).map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-xs">
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-[#0B2545] text-white font-mono text-[10px] flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[#1F2937] leading-relaxed">{r.action_fr}</div>
                      <div className="mt-1 flex items-center gap-3 text-[10px] font-mono text-[#5B5B62]">
                        <span>~{r.effort_weeks} sem.</span>
                        <span>·</span>
                        <span>impact +{r.weight}</span>
                        {r.actual && r.target && (
                          <>
                            <span>·</span>
                            <span className="text-amber-700">{r.actual}</span>
                            <span className="opacity-60">→ {r.target}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function GradeBadge({ grade }) {
  const map = {
    A: "bg-emerald-600 text-white",
    B: "bg-emerald-500 text-white",
    C: "bg-[#C5A880] text-white",
    D: "bg-amber-500 text-white",
    E: "bg-red-500 text-white",
    "—": "bg-[#E4E1D8] text-[#5B5B62]",
  };
  return (
    <span className={`inline-flex items-center justify-center h-7 w-7 rounded-md font-display font-bold text-sm ${map[grade] || map["—"]}`}
          data-testid={`grade-badge-${grade}`}>
      {grade}
    </span>
  );
}

export default DossierInstitutions;
